/**
 * As frases. Uma por regra, escritas à mão, revisadas como texto.
 *
 * Este arquivo é a razão de o `Achado` não ter campo de texto no backend. A escolha de palavras de
 * uma ferramenta de investigação É a ferramenta: «esteve 3h na casa de Fulano» e «esteve 3h nos
 * arredores de Fulano» são a mesma medida e duas afirmações diferentes, e só uma delas é
 * verdadeira. Em Python isso seria re-litigado a cada domínio novo.
 *
 * **Regra sem frase aqui fica invisível na tela e grita no console em dev.** Frase montada por
 * máquina numa ferramenta assim é pior que silêncio: o operador não tem como saber que a leitura
 * que ele está fazendo foi gerada por concatenação.
 */
import type { ReactNode } from 'react';
import type { Achado } from './tipos';

/** Renderiza a i-ésima entidade citada como link. A frase não monta link — só diz onde ele entra. */
export type Alvo = (indice: number) => ReactNode;

const reais = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const pct = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

/** Os cedentes de um grupo, citados em sequência: «A, B e C». */
function lista(a: Achado, alvo: Alvo, de = 0): ReactNode {
  const partes = a.entidades.slice(de).map((_, i) => alvo(de + i));
  return partes.map((p, i) => (
    <span key={i}>
      {i > 0 && (i === partes.length - 1 ? ' e ' : ', ')}
      {p}
    </span>
  ));
}

function duracao(seg: number): string {
  const h = Math.floor(seg / 3600);
  const m = Math.round((seg % 3600) / 60);
  return h ? `${h}h${String(m).padStart(2, '0')}` : `${m} min`;
}

export const FRASES: Record<string, (a: Achado, alvo: Alvo) => ReactNode> = {
  // «nos arredores de», e não «em». A parada não é um ponto: o aparelho parado espalha o fix por
  // uma ou duas centenas de metros, e o lugar é a mediana desse espalhamento. Dizer «ficou 3h na
  // casa de Fulano» afirmaria uma precisão de porta que não existe — a casa do vizinho está dentro
  // do mesmo raio. «Nos arredores» é o que se sabe, e quem lê continua tirando a conclusão certa
  // sem receber uma exatidão inventada.
  'rastreamento.parada_relevante': (a, alvo) => (
    <>
      Esteve <span className="font-semibold">{duracao(a.numeros.duracao_seg ?? 0)}</span> nos
      arredores de {alvo(0)}
    </>
  ),

  // Parentesco. A palavra muda com a VIA, não com o gosto: «é irmão de» quando os dois apontam
  // para a mesma mãe no cadastro, «pode ser irmão de» quando o que bate é o nome dela. A primeira
  // é leitura de registro; a segunda é semelhança de texto, e dizer as duas igual seria mentir com
  // confiança — o defeito que a classificação forte/fraco existe para impedir.
  'parentesco.irmaos_por_mae_comum': (a, alvo) =>
    a.evidencia.via === 'mae_no_grafo' ? (
      <>
        É <span className="font-semibold">irmão(ã)</span> de {alvo(0)} — os dois têm {alvo(1)} como
        genitor(a) no cadastro
      </>
    ) : (
      <>
        Pode ser <span className="font-semibold">irmão(ã)</span> de {alvo(0)}: mesma mãe declarada
        {a.numeros.mesmo_nome_de_mae > 1 && (
          <span className="text-text-secondary">
            {' '}
            (e mais {a.numeros.mesmo_nome_de_mae - 1} com o mesmo nome de mãe)
          </span>
        )}
      </>
    ),

  // «Pode ser da família», nunca «é da família». Morar junto com alguém de sobrenome raro é bom
  // motivo para perguntar: pode ser cônjuge, pode ser o sobrenome do marido, pode ser coincidência.
  'parentesco.familia_por_sobrenome_e_endereco': (a, alvo) => (
    <>
      Pode ser da <span className="font-semibold">família</span> de {alvo(0)}: mesmo sobrenome
      {' '}«{String(a.evidencia.detalhe.sobrenome ?? '')}»{' '}
      <span className="text-text-secondary">
        ({a.numeros.mesmo_sobrenome} na base) e mesmo endereço ({a.numeros.moradores} moradores)
      </span>
    </>
  ),

  // Fato patrimonial quando o laço está registrado; suspeita quando é só o sobrenome. E nenhuma
  // das duas diz por quê: «transferiu» é verificável nas linhas, «blindou patrimônio» é conclusão
  // sobre pessoas — e não é o software que a tira.
  'parentesco.transferencia_entre_parentes': (a, alvo) =>
    a.evidencia.via === 'parente_declarado' ? (
      <>
        O veículo {alvo(1)} passou entre esta pessoa e {alvo(0)},{' '}
        <span className="font-semibold">parente declarado</span>
      </>
    ) : (
      <>
        O veículo {alvo(1)} passou entre esta pessoa e {alvo(0)}, de{' '}
        <span className="font-semibold">mesmo sobrenome raro</span>
      </>
    ),

  'rastreamento.passagem_recorrente': (a, alvo) => (
    <>
      Passou <span className="font-semibold">{a.numeros.vezes}×</span> por {alvo(0)}{' '}
      <span className="text-text-secondary">em {a.numeros.dias} dias diferentes</span>
    </>
  ),

  // --- Crédito -------------------------------------------------------------------------------
  // Todas nomeiam RELAÇÃO e nunca INTENÇÃO. «Cedente e sacado no mesmo grupo» é um fato sobre duas
  // linhas do cadastro; «fraude» é uma conclusão sobre pessoas, e não é o software que a tira. Não
  // é diplomacia: um achado que conclui não pode ser conferido, e o que não se confere não serve
  // numa investigação.

  'credito.concentracao_por_nucleo': (a, alvo) => (
    <>
      Um único grupo econômico responde por{' '}
      <span className="font-semibold">{pct(a.numeros.fatia)}</span> da carteira —{' '}
      {reais(a.numeros.valor_nominal)} em {a.numeros.cedentes} cedentes: {lista(a, alvo)}
    </>
  ),

  'credito.concentracao_por_cedente': (a, alvo) => (
    <>
      {alvo(0)} responde por <span className="font-semibold">{pct(a.numeros.fatia)}</span> da
      carteira <span className="text-text-secondary">({reais(a.numeros.valor_nominal)})</span>
    </>
  ),

  'credito.cedente_sacado_mesmo_nucleo': (a, alvo) => (
    <>
      {alvo(0)} cedeu {a.numeros.titulos} título(s) contra {alvo(1)},{' '}
      <span className="font-semibold">do mesmo grupo econômico</span> —{' '}
      {reais(a.numeros.valor_nominal)}, {pct(a.numeros.fatia)} da carteira
    </>
  ),

  'credito.sacado_e_socio_do_cedente': (a, alvo) => (
    <>
      {alvo(0)} deve {reais(a.numeros.valor_nominal)} a {alvo(1)} e é{' '}
      <span className="font-semibold">sócio</span> dela
    </>
  ),

  'credito.sacado_pf_compartilhado': (a, alvo) => (
    <>
      {alvo(0)} deve a <span className="font-semibold">{a.numeros.cedentes} cedentes</span>{' '}
      diferentes desta carteira — {reais(a.numeros.valor_nominal)}
    </>
  ),

  'credito.situacao_cadastral_irregular': (a, alvo) => (
    <>
      {alvo(0)} não está ativa na Receita
      <span className="text-text-secondary">
        {' '}
        — {reais(a.numeros.valor_nominal)} na carteira
      </span>
    </>
  ),

  'credito.duplicata_mercantil_contra_pf': (a, alvo) => (
    <>
      {a.numeros.titulos} duplicata(s) mercantil(is) sacada(s) contra {alvo(0)},{' '}
      <span className="text-text-secondary">que é pessoa física</span> —{' '}
      {reais(a.numeros.valor_nominal)}
    </>
  ),

  'credito.vencido_ha_mais_de': (a, alvo) => (
    <>
      {alvo(0)} tem {a.numeros.titulos} título(s) vencido(s) há mais de{' '}
      <span className="font-semibold">{a.numeros.dias} dias</span> na data da posição —{' '}
      {reais(a.numeros.valor_nominal)}
    </>
  ),

  'credito.taxa_fora_de_escala': (a, alvo) => (
    <>
      Taxa de <span className="font-semibold">{a.numeros.taxa}</span> — {a.numeros.vezes}× a
      mediana desta posição ({a.numeros.mediana}) — no título {alvo(0)}, de {alvo(1)}
    </>
  ),

  'credito.aquisicao_acima_do_nominal': (a, alvo) => (
    <>
      O fundo pagou {reais(a.numeros.valor_aquisicao)} por um título de face{' '}
      {reais(a.numeros.valor_nominal)} <span className="font-semibold">({a.numeros.razao}×)</span>{' '}
      — {alvo(0)}, de {alvo(1)}
    </>
  ),

  'credito.divergencia_com_total_declarado': (a) => (
    <>
      A soma dos títulos ({reais(a.numeros.somado)}) não bate com o total que o arquivo declara (
      {reais(a.numeros.declarado)}) —{' '}
      <span className="font-semibold">{reais(Math.abs(a.numeros.diferenca))}</span> de diferença
    </>
  ),

  // FRACO porque repactuar é rotina de crédito — a leitura é «o dinheiro não voltou», e não uma
  // acusação. O que a frase acrescenta é o que «saiu um, entrou outro» esconde.
  'credito.rolagem_de_parcela': (a, alvo) => (
    <>
      {a.numeros.titulos} título(s) de {alvo(0)} contra {alvo(1)} saíram e voltaram com vencimento
      até <span className="font-semibold">{a.numeros.dias} dias adiante</span> —{' '}
      {reais(a.numeros.valor_nominal)} que não foram pagos, e sim empurrados
    </>
  ),

  // FRACO de propósito: pode ser parcela do mesmo contrato — o caso mais comum e inteiramente
  // normal — ou o mesmo papel cedido duas vezes. A frase mostra o par sem nomear qual dos dois é.
  'credito.titulos_gemeos': (a, alvo) => (
    <>
      {a.numeros.titulos} títulos de {reais(a.numeros.valor_nominal)} entre {alvo(0)} e {alvo(1)}{' '}
      <span className="text-text-secondary">— parcelas do mesmo contrato, ou não</span>
    </>
  ),
};

/** A frase de um achado, ou `null` quando a regra não tem frase escrita. */
export function frase(a: Achado, alvo: Alvo): ReactNode | null {
  const f = FRASES[a.regra];
  if (!f) {
    if (import.meta.env.DEV) {
      console.error(
        `[achados] regra sem frase: ${a.regra}. Escreva-a em lib/achados/frases.tsx — ` +
          'até lá ela fica invisível na tela.',
      );
    }
    return null;
  }
  return f(a, alvo);
}
